import classnames from 'classnames'
import { css } from 'emotion'
import { ConfigServiceDataKeys, LifeInsuranceStep, Product, QueryParamKeys } from 'even-ts-static'
import * as React from 'react'
import { connect } from 'react-redux'

import { stepSelected } from 'common/actions'
import { MultiProductDispatch } from 'embed/constants'
import { MultiProductState } from 'embed/reducers'

import { AdvertiserDisclosureContent } from 'embed/components/AdvertiserDisclosure/'
import DepositsDisclosure from 'embed/components/DepositsDisclosure'
import { LifeInsuranceAdvertiserDisclosure, LifeInsuranceDisclosure } from 'embed/components/LifeInsuranceDisclosure'
import LoanDisclosure from 'embed/components/LoanDisclosure'
import Modal from 'embed/components/Modal'
import ThemeConsumer from 'embed/components/ThemeConsumer'
import {
  configPartnerDisclaimerSelector,
  configStateSelector,
  isDotDashSelector,
  isFionaBrandedSelector,
} from 'embed/selectors/config'
import Parent from 'common/services/Parent'

import { Experiment, Original, TrustBadgesInFooter, Variant, withExperiment } from 'embed/experiments'
import { isStepByStepSelector, uiSelectedProductSelector, uiShouldHaveTrustBadgesSelector } from 'embed/selectors/ui'

const SSLBadge = require('./TrustBadges/ssl-badge.svg')
const PrivacyBadge = require('./TrustBadges/privacy-badge.png')
const BBBRatingABadge = require('./TrustBadges/bbb-badge-a.png')
const McAfeeBadge = require('./TrustBadges/mcafee-badge.png')

const styles = require('./styles')

export interface StoreProps {
  hideFooter: boolean
  inPartnershipWithFiona: boolean
  isDotDash: boolean
  isFionaBranded: boolean
  isStepByStep: boolean
  partnerDisclaimer?: string
  selectedProduct: Product
  showTrustBadges: boolean
}

interface DispatchProps {
  dispatchLifeInsuranceStepSelectedRequestACall: () => void
}

type Props = StoreProps & DispatchProps

const Footer: React.SFC<Props> = ({
  dispatchLifeInsuranceStepSelectedRequestACall,
  hideFooter,
  inPartnershipWithFiona,
  isDotDash,
  isFionaBranded,
  isStepByStep,
  partnerDisclaimer,
  selectedProduct,
  showTrustBadges,
}) => {
  const [modalVisible, setModalVisible] = React.useState(false)
  const toggleModalVisible = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()
    Parent.scrollToFrame()
    setModalVisible(!modalVisible)
  }

  let productDisclosure
  let productAdvertiserDisclosure = <AdvertiserDisclosureContent />
  switch (selectedProduct) {
    case Product.Deposits:
      productDisclosure = <DepositsDisclosure />
      break
    case Product.LifeInsurance:
      productAdvertiserDisclosure = LifeInsuranceAdvertiserDisclosure
      productDisclosure = <LifeInsuranceDisclosure />
      break
    case Product.Loan:
      productDisclosure = <LoanDisclosure />
      break
    default:
  }

  const isPersonalLoanOffer = selectedProduct === Product.Loan
  const isLifeInsuranceProduct = selectedProduct === Product.LifeInsurance

  const TrustBadges: React.FunctionComponent = () => (
    <div className={styles.trustedBadgesContainer}>
      <img
        alt="Equal Housing Opportunity"
        className={classnames(styles.housingLogo, css(`height: 26px;`))}
        src="https://images.evenfinancial.com/icons/eho-black.png"
      />
      <img src={McAfeeBadge} alt="McAfee Secure Badge" height="24" />
      <img src={BBBRatingABadge} alt="BBB A Rated Badge" height="26" />
      <img src={PrivacyBadge} alt="Verified Privacy Badge" height="27" />
      <SSLBadge />
    </div>
  )

  const LifeInsuranceTrustBadges: React.FunctionComponent = () => (
    <div className={styles.trustedBadgesContainer}>
      <img src={McAfeeBadge} alt="McAfee Secure Badge" height="24" />
      <img src={PrivacyBadge} alt="Verified Privacy Badge" height="27" />
      <SSLBadge />
    </div>
  )

  const TrustBadgesExperiment = withExperiment(TrustBadgesInFooter)(TrustBadges)

  const disclosures = (
    <React.Fragment>
      {productAdvertiserDisclosure}
      {productDisclosure}
    </React.Fragment>
  )

  const onLifeInsuranceSupportClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()
    dispatchLifeInsuranceStepSelectedRequestACall()
  }

  const footerLinksLifeInsurance = (
    <>
      <a href="https://www.leaplife.com/terms" target="_blank">
        Terms
      </a>{' '}
      <a href="https://www.leaplife.com/privacy" target="_blank">
        Privacy
      </a>{' '}
      <a href="#" onClick={onLifeInsuranceSupportClick}>
        Support
      </a>{' '}
    </>
  )

  const footerLinksFiona = (
    <>
      <a href="https://fiona.com/terms" target="_blank">
        Terms
      </a>{' '}
      <a href="https://fiona.com/privacy" target="_blank">
        Privacy
      </a>{' '}
      <a href="https://fiona.com/contact" target="_blank">
        Support
      </a>{' '}
    </>
  )

  const footerLinks = (
    <>
      <a href="https://evenfinancial.com/terms-of-service/" target="_blank">
        Terms
      </a>{' '}
      <a href="https://evenfinancial.com/privacy-policy/" target="_blank">
        Privacy
      </a>{' '}
      <a href="https://evenfinancial.com/contact/" target="_blank">
        Support
      </a>{' '}
    </>
  )

  return (
    <React.Fragment>
      {!hideFooter && (
        <ThemeConsumer>
          {({ colorTextPrimary, colorTextSecondary }) => (
            <div
              className={classnames(
                styles.footer,
                css(`
                  color: ${colorTextSecondary};

                  a { color: ${colorTextSecondary}; }
                  a:hover { color: ${colorTextPrimary}; }
                `),
                {
                  [css`
                    padding: 0 20px;
                  `]: isDotDash,
                },
              )}
            >
              <>
                {partnerDisclaimer && (
                  <div className={styles.partnerDisclaimer}>
                    <p>{partnerDisclaimer}</p>
                  </div>
                )}
                <div className={classnames(styles.footerContainer, isDotDash && isStepByStep && styles.stepByStep)}>
                  <div className={styles.left}>
                    {isLifeInsuranceProduct
                      ? footerLinksLifeInsurance
                      : inPartnershipWithFiona
                      ? footerLinksFiona
                      : footerLinks}
                    <a href="#" onClick={toggleModalVisible}>
                      Disclosures
                    </a>
                    {modalVisible && <Modal onDismiss={toggleModalVisible}>{disclosures}</Modal>}
                    {/* Remove !isPersonalLoanOffer from conditional after removing TrustedBadgesInFooter experiment code */}
                    {!isLifeInsuranceProduct && !isPersonalLoanOffer && (
                      <img
                        alt="Equal Housing Opportunity"
                        className={styles.housingLogo}
                        src="https://images.evenfinancial.com/icons/eho-black.png"
                      />
                    )}
                  </div>
                  <div className={styles.right}>
                    {isFionaBranded && !isLifeInsuranceProduct && (
                      <span className={styles.inPartnershipWith}>
                        in partnership with{' '}
                        <a href="https://fiona.com" target="_blank">
                          Fiona
                        </a>
                      </span>
                    )}
                    {isLifeInsuranceProduct && (
                      <span className={styles.inPartnershipWith}>
                        in partnership with{' '}
                        <a href="https://www.leaplife.com" target="_blank">
                          LeapLife
                        </a>
                      </span>
                    )}
                    <span className={styles.poweredByEven}>
                      powered by{' '}
                      <a href="https://evenfinancial.com/" target="_blank">
                        Even
                      </a>
                    </span>
                  </div>
                </div>
                {isPersonalLoanOffer && (
                  <Experiment experiment={TrustBadgesInFooter}>
                    <Original>
                      <></>
                    </Original>
                    <Variant experiment={TrustBadgesInFooter} variant={TrustBadgesInFooter.variants.EmbedWithBadges}>
                      <TrustBadgesExperiment />
                    </Variant>
                  </Experiment>
                )}
                {showTrustBadges && !isLifeInsuranceProduct && <TrustBadges />}
                {showTrustBadges && isLifeInsuranceProduct && <LifeInsuranceTrustBadges />}
              </>
            </div>
          )}
        </ThemeConsumer>
      )}
    </React.Fragment>
  )
}

const mapStateToProps = (state: MultiProductState) => ({
  hideFooter: configStateSelector(state)[QueryParamKeys.HideFooter],
  inPartnershipWithFiona: configStateSelector(state)[ConfigServiceDataKeys.inPartnershipWithFiona],
  isDotDash: isDotDashSelector(state),
  isFionaBranded: isFionaBrandedSelector(state),
  isStepByStep: isStepByStepSelector(state),
  partnerDisclaimer: configPartnerDisclaimerSelector(state),
  selectedProduct: uiSelectedProductSelector(state),
  showTrustBadges: uiShouldHaveTrustBadgesSelector(state),
})

const mapDispatchToProps = (dispatch: MultiProductDispatch) => ({
  dispatchLifeInsuranceStepSelectedRequestACall: () => {
    dispatch(stepSelected(Product.LifeInsurance, LifeInsuranceStep.RequestACall))
  },
})

export default connect(mapStateToProps, mapDispatchToProps)(Footer)
